import { useState } from 'react'
import { Card, Button, Table, Form, Row, Col, Modal, Spinner, Alert, Badge } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { formatCurrency } from '../../utils/formatters'
import { extractApiErrorMessage } from '../../utils/apiErrors'

const SaldosAFavorPanel = ({ clients = [] }) => {
  const queryClient = useQueryClient()
  const { clientId: emisorId, setClientId: setEmisorId, selectDisabled } = useSyncedLocalClientId()
  const [soloDisponibles, setSoloDisponibles] = useState(true)
  const [showApply, setShowApply] = useState(false)
  const [saldoSel, setSaldoSel] = useState(null)
  const [facturaId, setFacturaId] = useState('')
  const [monto, setMonto] = useState('')
  const [notas, setNotas] = useState('')

  const { data: saldosData, isLoading } = useQuery(
    ['saldos-a-favor', emisorId, soloDisponibles],
    () => {
      const params = new URLSearchParams()
      if (emisorId) params.append('client_id', emisorId)
      params.append('solo_disponibles', soloDisponibles ? 'true' : 'false')
      return api.get(`/financial/invoices/saldos-a-favor/?${params}`).then((r) => r.data)
    }
  )

  const { data: reporte } = useQuery(
    ['reporte-nc-saldos', emisorId],
    () => {
      const params = emisorId ? `?client_id=${emisorId}` : ''
      return api.get(`/financial/invoices/reporte-nc-saldos/${params}`).then((r) => r.data)
    }
  )

  const { data: facturasDestino, isLoading: loadingFacturas } = useQuery(
    ['facturas-destino-saf', saldoSel?.emisor_id, saldoSel?.receptor_rfc],
    () =>
      api
        .get(`/financial/invoices/facturas-para-nota-credito/?client_id=${saldoSel.emisor_id}`)
        .then((r) => r.data),
    { enabled: !!saldoSel?.emisor_id }
  )

  const facturasFiltradas = (facturasDestino?.results || []).filter(
    (f) =>
      (f.receptor_rfc || '').toUpperCase() === (saldoSel?.receptor_rfc || '').toUpperCase() &&
      parseFloat(f.saldo_disponible || 0) > 0
  )

  const aplicarMutation = useMutation(
    async () => {
      const body = {
        saldo_id: saldoSel.id,
        factura_id: parseInt(facturaId, 10),
        notas,
      }
      if (monto) body.monto = monto
      const res = await api.post('/financial/invoices/aplicar-saldo-a-favor/', body)
      return res.data
    },
    {
      onSuccess: (data) => {
        toast.success(`Aplicados ${formatCurrency(data.monto_aplicado)} a ${data.factura_folio}`)
        queryClient.invalidateQueries('saldos-a-favor')
        queryClient.invalidateQueries('reporte-nc-saldos')
        queryClient.invalidateQueries('invoices')
        setShowApply(false)
        setSaldoSel(null)
        setFacturaId('')
        setMonto('')
        setNotas('')
      },
      onError: (err) => toast.error(extractApiErrorMessage(err, 'No se pudo aplicar el saldo')),
    }
  )

  const saldos = saldosData?.results || []
  const totales = reporte?.totales || {}

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Saldos a favor y notas de crédito</h3>
          <p className="text-muted mb-0 small">
            Aplique saldos a favor a facturas pendientes. Consulte NC y aplicaciones.
          </p>
        </div>
      </div>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Emisor</Form.Label>
            <Form.Select value={emisorId} onChange={(e) => setEmisorId(e.target.value)} disabled={selectDisabled}>
              <option value="">Todos</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razon_social || c.business_name || c.rfc}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            label="Solo saldos con disponible"
            checked={soloDisponibles}
            onChange={(e) => setSoloDisponibles(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body className="py-2">
              <h5 className="mb-0">{totales.nc_timbradas_count ?? '—'}</h5>
              <small className="text-muted">NC timbradas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body className="py-2">
              <h5 className="mb-0">{formatCurrency(totales.nc_total_monto || 0)}</h5>
              <small className="text-muted">Monto NC</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center border-success">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{formatCurrency(totales.saldos_a_favor_disponibles || 0)}</h5>
              <small className="text-muted">Saldos a favor disponibles</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-2">Saldos a favor</h5>
      {isLoading ? (
        <Spinner size="sm" />
      ) : saldos.length === 0 ? (
        <Alert variant="secondary">No hay saldos a favor{soloDisponibles ? ' disponibles' : ''}.</Alert>
      ) : (
        <Table responsive striped bordered size="sm" className="mb-4">
          <thead>
            <tr>
              <th>Receptor</th>
              <th>NC origen</th>
              <th>Factura origen</th>
              <th className="text-end">Monto</th>
              <th className="text-end">Disponible</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {saldos.map((s) => (
              <tr key={s.id}>
                <td>
                  <small>{s.receptor_rfc}</small>
                  {s.receptor_nombre && <div className="small text-muted">{s.receptor_nombre}</div>}
                  {s.autorizacion_especial && <Badge bg="warning" text="dark">Aut. especial</Badge>}
                </td>
                <td>{s.nota_credito_folio || '—'}</td>
                <td>{s.factura_origen_folio || '—'}</td>
                <td className="text-end">{formatCurrency(s.monto)}</td>
                <td className="text-end"><strong>{formatCurrency(s.monto_disponible)}</strong></td>
                <td>
                  {parseFloat(s.monto_disponible) > 0 && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setSaldoSel(s)
                        setFacturaId('')
                        setMonto('')
                        setNotas('')
                        setShowApply(true)
                      }}
                    >
                      Aplicar a factura
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <h5 className="mb-2">Aplicaciones de saldo a favor</h5>
      <Table responsive striped bordered size="sm" className="mb-4">
        <thead>
          <tr>
            <th>Factura</th>
            <th>Saldo ID</th>
            <th className="text-end">Monto</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {(reporte?.aplicaciones_saldo_a_favor || []).map((a) => (
            <tr key={a.id}>
              <td>{a.factura_folio}</td>
              <td>{a.saldo_id}</td>
              <td className="text-end">{formatCurrency(a.monto)}</td>
              <td className="small">{a.created_at ? new Date(a.created_at).toLocaleString('es-MX') : '—'}</td>
            </tr>
          ))}
          {(reporte?.aplicaciones_saldo_a_favor || []).length === 0 && (
            <tr><td colSpan={4} className="text-muted">Sin aplicaciones.</td></tr>
          )}
        </tbody>
      </Table>

      <h5 className="mb-2">Notas de crédito recientes</h5>
      <Table responsive striped bordered size="sm">
        <thead>
          <tr>
            <th>Folio</th>
            <th>Receptor</th>
            <th>Motivo</th>
            <th className="text-end">Total</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {(reporte?.notas_credito || []).map((n) => (
            <tr key={n.id}>
              <td>{n.folio}</td>
              <td className="small">{n.receptor_rfc}</td>
              <td>{n.motivo_nc || '—'}</td>
              <td className="text-end">{formatCurrency(n.total)}</td>
              <td><Badge bg={n.uuid ? 'success' : 'secondary'}>{n.status}</Badge></td>
            </tr>
          ))}
          {(reporte?.notas_credito || []).length === 0 && (
            <tr><td colSpan={5} className="text-muted">Sin NC.</td></tr>
          )}
        </tbody>
      </Table>

      <Modal show={showApply} onHide={() => setShowApply(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Aplicar saldo a favor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saldoSel && (
            <>
              <p className="small mb-2">
                Disponible: <strong>{formatCurrency(saldoSel.monto_disponible)}</strong> · Receptor{' '}
                {saldoSel.receptor_rfc}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Factura destino *</Form.Label>
                <Form.Select
                  value={facturaId}
                  onChange={(e) => setFacturaId(e.target.value)}
                  disabled={loadingFacturas}
                >
                  <option value="">{loadingFacturas ? 'Cargando…' : 'Seleccionar factura con saldo…'}</option>
                  {facturasFiltradas.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.invoice_number} — pendiente {formatCurrency(f.saldo_disponible)}
                    </option>
                  ))}
                </Form.Select>
                {facturasFiltradas.length === 0 && !loadingFacturas && (
                  <Form.Text className="text-muted">No hay facturas pendientes del mismo receptor.</Form.Text>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Monto (vacío = máximo posible)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApply(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!facturaId || aplicarMutation.isLoading}
            onClick={() => aplicarMutation.mutate()}
          >
            {aplicarMutation.isLoading ? 'Aplicando…' : 'Aplicar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SaldosAFavorPanel
